import { useState, useEffect, useRef } from "react";
import { authFetch } from "../../App";
import { toast } from "sonner";
import {
  Upload, FileText, FolderOpen, ChevronRight, Check, X, Trash2,
  Loader2, AlertTriangle, Link as LinkIcon,
} from "lucide-react";
import { Button } from "../../components/ui/button";

const API = "/api";

export function ImportList({ imports, loading, onSelect, onUpload, onRefresh, onAdoptFromList }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [adoptTarget, setAdoptTarget] = useState(null);
  const [projects, setProjects] = useState([]);
  const [adoptMode, setAdoptMode] = useState("new");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [blockName, setBlockName] = useState("");
  const [newProjectName, setNewProjectName] = useState("");
  const [adopting, setAdopting] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await authFetch(`${API}/gaeb/upload`, { method: "POST", body: fd });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.position_count} Positionen eingelesen`);
        onRefresh();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Upload fehlgeschlagen");
      }
    } catch { toast.error("Upload fehlgeschlagen"); }
    setUploading(false);
    e.target.value = "";
  };

  useEffect(() => {
    if (adoptTarget) {
      authFetch(`${API}/projects`).then(r => r.ok ? r.json() : []).then(setProjects);
      setBlockName(adoptTarget.lv_desc || adoptTarget.lv_name || "");
      setNewProjectName(adoptTarget.project_desc || adoptTarget.lv_desc || "");
      setAdoptMode("new");
      setSelectedProjectId("");
    }
  }, [adoptTarget]);

  const handleDelete = async (mode) => {
    if (!deleteTarget) return;
    try {
      const res = await authFetch(`${API}/gaeb/imports/${deleteTarget.id}?mode=${mode}`, { method: "DELETE" });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        onRefresh();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler beim Loeschen");
      }
    } catch { toast.error("Fehler beim Loeschen"); }
    setDeleteTarget(null);
  };

  const handleAdopt = async () => {
    if (!adoptTarget) return;
    setAdopting(true);
    try {
      const body = { block_name: blockName || undefined };
      if (adoptMode === "existing" && selectedProjectId) {
        body.project_id = selectedProjectId;
      } else {
        body.project_name = newProjectName;
      }
      const res = await authFetch(`${API}/gaeb/imports/${adoptTarget.id}/adopt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Block "${result.block_name}" hinzugefuegt`);
        onRefresh();
        if (onAdoptFromList) onAdoptFromList(result.project_id);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler");
      }
    } catch { toast.error("Fehler"); }
    setAdopting(false);
    setAdoptTarget(null);
  };

  const isAdopted = (imp) => imp.adoption_count > 0 || imp.status === "uebernommen";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">GAEB Importe</h2>
        <div>
          <input ref={fileRef} type="file" accept=".p83,.d83,.x83,.gaeb,.xlsx,.xls" className="hidden" onChange={handleFile} />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-2 bg-slate-800 hover:bg-slate-900" data-testid="gaeb-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Datei hochladen
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : imports.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Noch keine GAEB-Dateien importiert</p>
          <p className="text-xs text-gray-400 mt-1">Laden Sie eine .P83 oder .xlsx Datei hoch</p>
        </div>
      ) : (
        <div className="space-y-2">
          {imports.map((imp) => (
            <div key={imp.id} className="bg-white rounded-lg border border-gray-200 hover:border-slate-300 p-4 transition-colors group" data-testid={`gaeb-import-${imp.id}`}>
              <div className="flex items-center justify-between gap-3">
                <button onClick={() => onSelect(imp.id)} className="flex items-center gap-3 min-w-0 flex-1 text-left">
                  <FileText className="w-5 h-5 text-slate-500 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{imp.filename}</p>
                    <p className="text-xs text-gray-500 truncate">{imp.project_desc || imp.lv_desc}</p>
                    {imp.adopted_projects?.length > 0 && (
                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                        {imp.adopted_projects.map((ap) => (
                          <span key={ap.project_id} className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full font-medium">{ap.project_name}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </button>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${isAdopted(imp) ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                    {isAdopted(imp) ? `${imp.adoption_count || 1}x Projekt` : "Neu"}
                  </span>
                  <span className="text-xs text-gray-400">{imp.position_count} Pos.</span>
                  <Button size="sm" variant="outline" className="gap-1 text-[11px] h-7 border-green-200 text-green-700 hover:bg-green-50" onClick={(e) => { e.stopPropagation(); setAdoptTarget(imp); }} data-testid={`gaeb-adopt-list-${imp.id}`}>
                    <FolderOpen className="w-3 h-3" /> In Projekt
                  </Button>
                  <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(imp); }} className="p-1.5 hover:bg-red-50 rounded-lg transition-colors" title="GAEB loeschen" data-testid={`gaeb-delete-${imp.id}`}>
                    <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                  </button>
                  <button onClick={() => onSelect(imp.id)} className="p-1">
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* DELETE DIALOG */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDeleteTarget(null)} data-testid="delete-dialog-overlay">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="delete-dialog">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900">GAEB Datei loeschen</h3>
              <p className="text-xs text-gray-500 mt-1">"{deleteTarget.filename}"</p>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-gray-700">Moechten Sie diese GAEB Datei wirklich loeschen?</p>
              <p className="text-xs text-red-500 font-medium">Diese Aktion kann nicht rueckgaengig gemacht werden.</p>
              {isAdopted(deleteTarget) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800 font-medium flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4" /> Diese Datei ist {deleteTarget.adoption_count || 1}x einem Projekt zugeordnet
                  </p>
                  {deleteTarget.adopted_projects?.map((ap) => (
                    <p key={ap.project_id} className="text-xs text-amber-600 mt-1 ml-5.5">{ap.project_name}</p>
                  ))}
                </div>
              )}
            </div>
            <div className="px-5 py-3.5 border-t border-gray-100 flex flex-col gap-2">
              {isAdopted(deleteTarget) && (
                <Button variant="outline" onClick={() => handleDelete("unlink")} className="w-full gap-2 text-sm text-amber-700 border-amber-200 hover:bg-amber-50" data-testid="delete-unlink-btn">
                  <LinkIcon className="w-4 h-4" /> Nur Verknuepfung entfernen
                </Button>
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDeleteTarget(null)} className="flex-1 text-sm" data-testid="delete-cancel-btn">Abbrechen</Button>
                <Button onClick={() => handleDelete("full")} className="flex-1 gap-2 bg-red-600 hover:bg-red-700 text-sm" data-testid="delete-confirm-btn">
                  <Trash2 className="w-4 h-4" /> Komplett loeschen
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ADOPT DIALOG */}
      {adoptTarget && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setAdoptTarget(null)} data-testid="adopt-list-dialog-overlay">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="adopt-list-dialog">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900">In Projekt uebernehmen</h3>
              <p className="text-xs text-gray-500 mt-1">"{adoptTarget.filename}" — {adoptTarget.position_count} Positionen</p>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="flex bg-gray-100 rounded-lg p-0.5">
                <button onClick={() => setAdoptMode("new")} className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${adoptMode === "new" ? "bg-white shadow text-gray-900" : "text-gray-500"}`} data-testid="adopt-list-mode-new">Neues Projekt</button>
                <button onClick={() => setAdoptMode("existing")} className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${adoptMode === "existing" ? "bg-white shadow text-gray-900" : "text-gray-500"}`} data-testid="adopt-list-mode-existing">Bestehendes Projekt</button>
              </div>
              {adoptMode === "new" && (
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Projektname</label>
                  <input value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300" data-testid="adopt-list-project-name" />
                </div>
              )}
              {adoptMode === "existing" && (
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Projekt waehlen</label>
                  <select value={selectedProjectId} onChange={(e) => setSelectedProjectId(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 bg-white" data-testid="adopt-list-project-select">
                    <option value="">-- Projekt waehlen --</option>
                    {projects.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Block-Bezeichnung</label>
                <input value={blockName} onChange={(e) => setBlockName(e.target.value)} placeholder="z.B. Haupt-LV, Beleuchtung, Nachtrag 1" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300" data-testid="adopt-list-block-name" />
                <p className="text-[11px] text-gray-400 mt-1">Wird als separater Block im Projekt angezeigt</p>
              </div>
            </div>
            <div className="px-5 py-3.5 border-t border-gray-100 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAdoptTarget(null)} className="text-sm" data-testid="adopt-list-cancel">Abbrechen</Button>
              <Button onClick={handleAdopt} disabled={adopting || (adoptMode === "existing" && !selectedProjectId)} className="gap-2 bg-green-600 hover:bg-green-700 text-sm" data-testid="adopt-list-confirm">
                {adopting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Uebernehmen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
